import { useState } from "react";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card";
import { Button } from "@/modules/shared/ui/components/button";
import { Badge } from "@/modules/shared/ui/components/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/modules/shared/ui/components/table";
import { useWalletTransactions } from "../../hooks/use-wallet";

const transactionTypeBadge: Record<
  string,
  {
    label: string
    variant: "default" | "secondary" | "destructive" | "success" | "outline"
  }
> = {
  TopUp: { label: "Top Up", variant: "success" },
  Deduction: { label: "Deduction", variant: "secondary" },
  Refund: { label: "Refund", variant: "outline" },
  Adjustment: { label: "Adjustment", variant: "outline" },
}

export function TransactionHistory() {
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const { data, isLoading } = useWalletTransactions(page, pageSize);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const totalPages = data ? Math.ceil(data.totalCount / pageSize) : 0;

  return (
    <Card className="rounded-2xl border border-border shadow-lg bg-card">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground">
          Transaction History
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!data || data.items.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No transactions yet</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border">
                  <TableHead className="bg-linear-to-br from-muted to-muted/50 px-6 py-4 text-xs font-semibold text-foreground uppercase tracking-wider">Date</TableHead>
                  <TableHead className="bg-linear-to-br from-muted to-muted/50 px-6 py-4 text-xs font-semibold text-foreground uppercase tracking-wider">Description</TableHead>
                  <TableHead className="bg-linear-to-br from-muted to-muted/50 px-6 py-4 text-xs font-semibold text-foreground uppercase tracking-wider">Type</TableHead>
                  <TableHead className="bg-linear-to-br from-muted to-muted/50 px-6 py-4 text-xs font-semibold text-foreground uppercase tracking-wider text-right">Amount</TableHead>
                  <TableHead className="bg-linear-to-br from-muted to-muted/50 px-6 py-4 text-xs font-semibold text-foreground uppercase tracking-wider text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((tx) => (
                  <TableRow
                    key={tx.id}
                    className="border-b border-border hover:bg-muted/50 transition-colors duration-150 cursor-pointer"
                  >
                    <TableCell className="px-6 py-4 text-sm text-foreground">
                      {new Date(tx.createdAt).toLocaleDateString("en-ZA")}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm text-foreground font-medium max-w-[300px] truncate">
                      {tx.description}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm text-foreground">
                      <Badge variant={transactionTypeBadge[tx.type]?.variant ?? "outline"}>
                        {transactionTypeBadge[tx.type]?.label ?? tx.type}
                      </Badge>
                    </TableCell>
                    <TableCell className={`px-6 py-4 text-sm text-right font-semibold ${tx.totalAmount >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {tx.totalAmount >= 0 ? "+" : ""}R{Math.abs(tx.totalAmount).toFixed(2)}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm text-foreground text-right">
                      R{tx.balanceAfter.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Page {page} of {totalPages} ({data.totalCount} total)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
